import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, X } from "lucide-react";
import { SectionHeader, Reveal } from "./Ornament";

// Each production now has its own AI-generated photoreal reel.
const productions = [
  {
    id: "lego-brand-activation",
    slug: "lego-brand-activation",
    location: "Brand Experience",
    title: "LEGO — Build the Future",
    detail: "Immersive brand experience · Athens · 2025",
    image: "/generated/lego-arch-collage.png",
  },
  {
    id: "lipton-sunsync",
    slug: "lipton-sunsync",
    location: "Brand Experience",
    title: "LIPTON — SunSync",
    detail: "Day-to-Night brand journey · Athens Riviera · 2025",
    image: "/generated/lipton-sunset-arch.png",
  },
  {
    id: "secret-essential-oils",
    slug: "secret-essential-oils",
    location: "Brand Experience",
    title: "SECRET — Essential Oils",
    detail: "Sensorial influencer event · 2025",
    image: "/generated/secret-oils-bar.png",
  },
  {
    id: "marvis-for-kids",
    slug: "marvis-for-kids",
    location: "Brand Experience",
    title: "MARVIS — No Ordinary Smile",
    detail: "Fashion-led brand activation for kids · 2025",
    image: "/generated/marvis-dress-girl.png",
  },
  {
    id: "brand-experience",
    location: "Brand Experience",
    title: "Luxury Brand Experience",
    detail: "Signature brand activation · 2025",
    image: "/generated/concept-signature-installations.jpg",
    duration: "0:15",
    video: "/generated/prod-brand-experience.mp4",
  },
  {
    id: "villa-cap-ferrat",
    location: "Villa · Cap Ferrat",
    title: "A Golden Symphony",
    detail: "Private wedding · 260 guests · Oct 2025",
    image: "/generated/golden-performer-1.jpg",
    duration: "0:08",
    video: "/generated/prod-golden-symphony.mp4",
  },
  {
    id: "capri-yacht",
    location: "Yacht · Capri",
    title: "Sicilian Sunset Reprise",
    detail: "Brand activation · Prada · Aug 2025",
    image: "/generated/sicilian-dolce-2.jpg",
    duration: "0:27",
    video: "/generated/prod-sicilian-sunset.mp4",
  },
  {
    id: "athens-rooftop",
    location: "Rooftop · Athens",
    title: "White Island Nights",
    detail: "Corporate gala · 400 guests · Jul 2025",
    image: "/generated/white-party-1.jpg",
    duration: "0:08",
    video: "/generated/concept-white-party.mp4",
  },
  {
    id: "riyadh-palace",
    location: "Palace · Riyadh",
    title: "Arabian Nights Grand Opening",
    detail: "Five-star hotel launch · May 2025",
    image: "/generated/arabian-nights-2.jpg",
    duration: "0:08",
    video: "/generated/concept-arabian-nights.mp4",
  },
  {
    id: "milan-atelier",
    slug: "baroque-opulence",
    location: "Palazzo Visconti · Milano",
    title: "Baroque Opulence Dinner Show",
    detail: "Marie-Antoinette welcome dancers · Sep 2025",
    image: "/generated/baroque-banquet.jpg",
    duration: "0:08",
    video: "/generated/prod-baroque-opulence.mp4",
  },
  {
    id: "ibiza-cliff",
    location: "Cliff Villa · Ibiza",
    title: "Nymphs of the Sea",
    detail: "Anniversary celebration · Jun 2025",
    image: "/generated/sea-nymphs-yacht-hero.jpg",
    duration: "0:08",
    video: "/generated/concept-nymphs-of-the-sea.mp4",
  },
];

/* -------- Video lightbox modal -------- */
function VideoModal({ src, poster, title, onClose }) {
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-label={title || "Video"}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-[var(--lu-black)]/90 backdrop-blur-md"
      onClick={onClose}
      data-testid="video-modal"
    >
      <button
        onClick={onClose}
        aria-label="Close video"
        data-testid="video-modal-close"
        className="absolute top-6 right-6 w-11 h-11 rounded-full border border-[var(--lu-gold)] text-[var(--lu-gold)] flex items-center justify-center hover:bg-[var(--lu-gold)] hover:text-[var(--lu-black)] transition-colors"
      >
        <X size={20} />
      </button>
      <div
        className="relative w-full max-w-5xl aspect-video border border-[var(--lu-gold)]/60 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)] bg-black overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <video
          key={src}
          className="w-full h-full object-cover"
          src={src}
          poster={poster}
          controls
          autoPlay
          playsInline
          onError={() => setErrored(true)}
          data-testid="video-modal-player"
        />
        {errored && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[var(--lu-black)]/80 backdrop-blur-md text-center px-6">
            <p className="kicker">Reel in Production</p>
            <p className="mt-4 font-serif italic text-[22px] text-[var(--lu-gold-light)]">
              This reel is being finalised — check back soon.
            </p>
          </div>
        )}
        {title && (
          <div className="absolute -bottom-11 left-0 right-0 text-center">
            <p className="font-serif italic text-[18px] text-[var(--lu-gold-light)]">{title}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RecentProductions() {
  const [active, setActive] = useState(null); // production object or null
  const navigate = useNavigate();

  const handleClick = (p) => {
    if (p.slug) navigate(`/projects/${p.slug}`);
    else if (p.video) setActive(p);
  };

  return (
    <section
      id="recent-productions"
      className="relative bg-[var(--lu-black)] section-y grain"
      data-testid="recent-productions-section"
    >
      <div className="container-lux">
        <SectionHeader
          kicker="Recent Productions"
          title="Screenshots"
          italicTitle="from the season"
          sub="A sneak peek at some of our latest productions. Every night is bespoke — no two are the same."
          light
        />

        <div
          className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          data-testid="recent-productions-grid"
        >
          {productions.map((p, i) => (
            <Reveal delay={Math.min((i % 3) + 1, 6)} key={p.id}>
              <button
                type="button"
                onClick={() => handleClick(p)}
                disabled={!p.video && !p.slug}
                className="tile group aspect-[4/5] block w-full text-left disabled:cursor-default"
                data-testid={`production-${p.id}`}
                aria-label={p.slug ? `Open ${p.title}` : (p.video ? `Play ${p.title}` : p.title)}
              >
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                <div className="tile-cover-overlay" />

                {/* Play button — only when a real video is attached */}
                {p.video && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="relative w-24 h-24 rounded-full flex items-center justify-center">
                      <span className="absolute inset-0 rounded-full bg-[var(--lu-black)]/50 backdrop-blur-md" />
                      <span className="absolute inset-2 rounded-full bg-[var(--lu-gold)] play-pulse group-hover:scale-110 transition-transform duration-500 shadow-[0_20px_50px_-10px_rgba(201,160,80,0.8)]" />
                      <Play size={26} className="relative text-[var(--lu-black)] ml-1" fill="currentColor" />
                    </span>
                  </div>
                )}

                {/* View case-study pill — for cards linking to a detail page */}
                {p.slug && !p.video && (
                  <div className="absolute top-5 right-5 px-4 py-1.5 bg-[var(--lu-gold)] text-[var(--lu-black)] font-micro text-[10px] tracking-[0.32em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    View Case →
                  </div>
                )}

                {/* Duration pill — only if video attached */}
                {p.video && p.duration && (
                  <div className="absolute top-5 right-5 px-3 py-1 bg-[var(--lu-black)]/70 border border-[var(--lu-gold)]/50 backdrop-blur-md">
                    <p className="font-micro text-[9px] text-gold-light-90">{p.duration}</p>
                  </div>
                )}

                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="kicker">{p.location}</p>
                  <h3 className="mt-3 font-serif text-[24px] text-[var(--lu-ivory)] leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[12px] text-ivory-70 font-light">
                    {p.detail}
                  </p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 flex justify-center">
          <Link
            to="/experiences"
            className="btn-gold-outline"
            data-testid="see-portfolio-cta"
          >See All Experiences</Link>
        </div>
      </div>

      {active && (
        <VideoModal
          src={active.video}
          poster={active.image}
          title={`${active.title} — ${active.location}`}
          onClose={() => setActive(null)}
        />
      )}
    </section>
  );
}
